import { useState, useEffect, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import makeStyles from '@mui/styles/makeStyles';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { useDebounce } from 'react-use';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * Validation result for GitHub org name
 */
interface ValidationResult {
  isValid: boolean;
  message: string;
  type: 'error' | 'warning' | 'success' | 'info';
}

/**
 * Response from org existence check endpoint
 */
interface OrgExistenceResponse {
  exists: boolean;
  message: string;
}

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
  },
  helperContainer: {
    marginTop: theme.spacing(1),
  },
  guidelinesBox: {
    padding: theme.spacing(1.5),
    backgroundColor: `${theme.palette.info.light}20`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.info.light}`,
  },
  guidelinesTitle: {
    fontWeight: 600,
    color: theme.palette.info.dark,
    marginBottom: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  guidelinesText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    margin: 0,
    paddingLeft: theme.spacing(0.5),
  },
  guidelinesLink: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  charCounter: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textAlign: 'right',
    marginTop: theme.spacing(0.25),
  },
  charCounterError: {
    color: theme.palette.error.main,
  },
}));

/**
 * Schema for the GitHubOrgNamePicker field extension
 */
export interface GitHubOrgNamePickerSchema {
  title?: string;
  description?: string;
}

/**
 * UI Schema options for the GitHubOrgNamePicker
 */
interface GitHubOrgNamePickerUiSchema {
  'ui:options'?: {
    /** Placeholder text for the input field */
    placeholder?: string;
    /** Debounce delay in milliseconds for org existence check */
    debounceMs?: number;
    /** Whether to check org existence on input (default: true) */
    checkOrgExistence?: boolean;
    /** Custom guidelines text (optional, defaults to CARIAD guidelines) */
    guidelinesText?: string;
  };
}

// GitHub org name rules
const MAX_ORG_NAME_LENGTH = 39;
const ORG_NAME_PATTERN = /^[a-z0-9-]*$/;
const CONSECUTIVE_HYPHENS_PATTERN = /--/;
const STARTS_OR_ENDS_WITH_HYPHEN = /^-|-$/;

/**
 * Validates the GitHub organization name format
 */
export function validateOrgName(name: string): ValidationResult {
  if (!name) {
    return {
      isValid: false,
      message: 'Organization name is required',
      type: 'error',
    };
  }

  // Check for uppercase characters
  if (name !== name.toLowerCase()) {
    return {
      isValid: false,
      message: 'Organization name must be lowercase only',
      type: 'error',
    };
  }

  // Check for invalid characters
  if (!ORG_NAME_PATTERN.test(name)) {
    return {
      isValid: false,
      message: 'Only lowercase letters, numbers, and hyphens are allowed',
      type: 'error',
    };
  }

  // Check for consecutive hyphens
  if (CONSECUTIVE_HYPHENS_PATTERN.test(name)) {
    return {
      isValid: false,
      message: 'Consecutive hyphens (--) are not allowed',
      type: 'error',
    };
  }

  // Check if starts or ends with hyphen
  if (STARTS_OR_ENDS_WITH_HYPHEN.test(name)) {
    return {
      isValid: false,
      message: 'Organization name cannot start or end with a hyphen',
      type: 'error',
    };
  }

  // Check length
  if (name.length > MAX_ORG_NAME_LENGTH) {
    return {
      isValid: false,
      message: `Organization name cannot exceed ${MAX_ORG_NAME_LENGTH} characters`,
      type: 'error',
    };
  }

  // Check minimum length (GitHub requires at least 1 character)
  if (name.length < 1) {
    return {
      isValid: false,
      message: 'Organization name must be at least 1 character',
      type: 'error',
    };
  }

  return {
    isValid: true,
    message: 'Valid organization name format',
    type: 'success',
  };
}

/**
 * GitHubOrgNamePicker - A scaffolder field extension for entering GitHub organization names
 *
 * This component provides an input field that validates GitHub organization names
 * according to GitHub's naming rules and optionally checks if the org already exists.
 *
 * Validation rules:
 * - Only lowercase alphanumerical characters and hyphens allowed
 * - Maximum length is 39 characters
 * - Cannot start or end with a hyphen
 * - Cannot contain consecutive hyphens
 *
 * Features:
 * - Real-time format validation
 * - Optional org existence check via backend API
 * - CARIAD guidelines reminder
 * - Clear error messages
 */
export const GitHubOrgNamePicker = (
  props: FieldExtensionComponentProps<string, GitHubOrgNamePickerSchema>,
) => {
  const { onChange, required, formData, uiSchema, schema } = props;

  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [inputValue, setInputValue] = useState(formData || '');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [formatValidation, setFormatValidation] =
    useState<ValidationResult | null>(null);
  const [existenceCheck, setExistenceCheck] = useState<{
    loading: boolean;
    result: OrgExistenceResponse | null;
    error: string | null;
  }>({ loading: false, result: null, error: null });

  // Get UI options with defaults
  const uiOptions =
    (uiSchema as GitHubOrgNamePickerUiSchema)?.['ui:options'] ?? {};
  const placeholder = uiOptions.placeholder ?? 'e.g. my-organization';
  const debounceMs = uiOptions.debounceMs ?? 500;
  const checkOrgExistence = uiOptions.checkOrgExistence ?? true;
  const guidelinesText =
    uiOptions.guidelinesText ??
    'The organization name must comply with official CARIAD guidelines and GitHub naming conventions.';

  // Debounce the input value for existence check
  useDebounce(
    () => {
      setDebouncedValue(inputValue);
    },
    debounceMs,
    [inputValue],
  );

  // Check if org exists
  const checkOrgExists = useCallback(
    async (orgName: string) => {
      if (!orgName || !checkOrgExistence) {
        setExistenceCheck({ loading: false, result: null, error: null });
        return;
      }

      // Only check if format is valid
      const validation = validateOrgName(orgName);
      if (!validation.isValid) {
        setExistenceCheck({ loading: false, result: null, error: null });
        return;
      }

      setExistenceCheck({ loading: true, result: null, error: null });

      try {
        const baseUrl = await discoveryApi.getBaseUrl('githubemu');
        const response = await fetchApi.fetch(
          `${baseUrl}/github/org-exists?name=${encodeURIComponent(orgName)}`,
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            errorText || `Failed to check org: ${response.status}`,
          );
        }

        const result: OrgExistenceResponse = await response.json();
        setExistenceCheck({ loading: false, result, error: null });
      } catch (error) {
        setExistenceCheck({
          loading: false,
          result: null,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to check organization existence',
        });
      }
    },
    [checkOrgExistence, discoveryApi, fetchApi],
  );

  // Check org existence when debounced value changes
  useEffect(() => {
    if (debouncedValue) {
      checkOrgExists(debouncedValue);
    } else {
      setExistenceCheck({ loading: false, result: null, error: null });
    }
  }, [debouncedValue, checkOrgExists]);

  useEffect(() => {
    if (!inputValue) {
      onChange('');
      return;
    }

    const formatValid = validateOrgName(inputValue).isValid;
    const loading = existenceCheck.loading;
    const exists = existenceCheck.result?.exists;

    if (!formatValid || loading || exists) {
      onChange('');
    } else {
      onChange(inputValue);
    }
  }, [inputValue, existenceCheck, onChange]);
  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Convert to lowercase automatically
    const normalizedValue = value.toLowerCase();
    setInputValue(normalizedValue);

    // Validate format
    const validation = validateOrgName(normalizedValue);
    setFormatValidation(validation);
  };

  // Determine the overall validation state
  const getOverallValidation = (): ValidationResult | null => {
    if (!inputValue) return null;

    // Format validation takes precedence
    if (formatValidation && !formatValidation.isValid) {
      return formatValidation;
    }

    // Check existence result
    if (existenceCheck.loading) {
      return {
        isValid: true,
        message: 'Checking if organization name is available...',
        type: 'info',
      };
    }

    if (existenceCheck.error) {
      return {
        isValid: true, // Don't block on existence check errors
        message: `Unable to verify availability: ${existenceCheck.error}`,
        type: 'warning',
      };
    }

    if (existenceCheck.result?.exists) {
      return {
        isValid: false,
        message: 'This organization name already exists.',
        type: 'error',
      };
    }

    if (existenceCheck.result && !existenceCheck.result.exists) {
      return {
        isValid: true,
        message: 'Organization name is available.',
        type: 'success',
      };
    }

    return formatValidation;
  };

  const overallValidation = getOverallValidation();
  const hasError = Boolean(
    // (rawErrors && rawErrors.length > 0) ||
    overallValidation && !overallValidation.isValid,
  );

  // Build helper text based on validation state
  const getHelperText = () => {
    // if (rawErrors && rawErrors.length > 0) {
    //   return rawErrors[0];
    // }
    if (overallValidation && inputValue) {
      const colorMap: Record<string, string> = {
        success: 'success.main',
        warning: 'warning.main',
        info: 'info.main',
        error: 'inherit',
      };
      const icon = overallValidation.type === 'success' ? '✓ ' : '';
      return (
        <Typography
          component="span"
          sx={{
            color: colorMap[overallValidation.type] || 'inherit',
            fontSize: 'inherit',
          }}
        >
          {icon}
          {overallValidation.message}
        </Typography>
      );
    }
    return schema.description || '';
  };

  return (
    <div className={classes.container}>
      {/* Guidelines Box */}
      <Box className={classes.guidelinesBox}>
        <Typography component="p" className={classes.guidelinesText}>
          {guidelinesText}{' '}
          <Link
            href="https://docs.mega.cariad.cloud/megatron/megatron2/networking/domains/#domain-name-guidelines"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.guidelinesLink}
          >
            View official guidelines
          </Link>
        </Typography>
      </Box>

      {/* Input Field */}
      <TextField
        label={schema.title || 'GitHub Organization Name'}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        required={required}
        error={hasError}
        helperText={getHelperText()}
        variant="outlined"
        fullWidth
        inputProps={{
          maxLength: MAX_ORG_NAME_LENGTH,
        }}
        InputProps={{
          endAdornment: existenceCheck.loading ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
      />

      {/* Character Counter */}
      <Typography
        className={`${classes.charCounter} ${
          inputValue.length > MAX_ORG_NAME_LENGTH
            ? classes.charCounterError
            : ''
        }`}
      >
        {inputValue.length}/{MAX_ORG_NAME_LENGTH}
      </Typography>
    </div>
  );
};
